import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { CheckCircle2, MapPin, Mail } from "lucide-react";
import axios from "axios";
import { useLang } from "@/lib/lang";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const { lang } = useLang();
  const T = lang === "fr" ? FR : EN;
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/store/orders/${orderNumber}`)
      .then(r => setOrder(r.data))
      .catch(() => setError(true));
  }, [orderNumber]);

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="order-success-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#8b6f3d]">
            <MapPin size={18} /><span className="font-mono-data text-xs uppercase tracking-widest">Atlas · Store</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-16">
        {error ? (
          <div className="text-center" data-testid="order-not-found">
            <h1 className="font-serif-mystery text-3xl mb-4">{T.notFound}</h1>
            <Link to="/store"><Button className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-12 px-6">{T.backToStore}</Button></Link>
          </div>
        ) : !order ? (
          <p className="text-center text-zinc-500">{T.loading}</p>
        ) : (
          <>
            <div className="text-center mb-10">
              <div className="mx-auto w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <CheckCircle2 size={40} className="text-green-600" />
              </div>
              <div className="font-mono-data text-[10px] uppercase tracking-[0.3em] text-[#8b6f3d] mb-2">{T.tag}</div>
              <h1 className="font-serif-mystery text-4xl mb-3" data-testid="order-success-title">{T.title}</h1>
              <p className="text-zinc-600 text-lg">{T.body}</p>
              <p className="mt-4 font-mono-data text-sm">
                {T.orderRef} <strong className="text-[#8b6f3d]" data-testid="order-number">{order.order_number}</strong>
              </p>
            </div>

            <div className="border border-zinc-200 rounded-2xl p-6 mb-6" data-testid="order-details">
              <h3 className="font-serif-mystery text-xl mb-4 text-[#8b6f3d]">{T.itemsTitle}</h3>
              <div className="space-y-3">
                {order.items.map(it => (
                  <div key={`${it.product_id}-${it.size}`} className="flex gap-4 border-b border-zinc-100 pb-3 last:border-0">
                    <img src={it.image} alt="" className="w-16 h-16 object-cover rounded-lg" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{lang === "fr" ? it.name_fr : it.name_en}</div>
                      <div className="text-xs text-zinc-500">{T.size}: {it.size} · ×{it.quantity}</div>
                    </div>
                    <div className="font-medium">{it.line_total.toFixed(2)} {order.currency}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-200 space-y-1 text-sm">
                <div className="flex justify-between text-zinc-600"><span>{T.subtotal}</span><span>{order.subtotal.toFixed(2)} {order.currency}</span></div>
                <div className="flex justify-between text-zinc-600"><span>{T.shipping}</span><span>{order.shipping_fee.toFixed(2)} {order.currency}</span></div>
                <div className="flex justify-between text-lg font-medium pt-2 border-t border-zinc-100">
                  <span>Total</span><span className="text-[#8b6f3d]">{order.total.toFixed(2)} {order.currency}</span>
                </div>
              </div>
            </div>

            <div className="border border-zinc-200 rounded-2xl p-6 mb-6" data-testid="order-shipping">
              <h3 className="font-serif-mystery text-xl mb-3 text-[#8b6f3d]">{T.shippingAddr}</h3>
              <p className="text-sm leading-relaxed">
                {order.shipping.full_name}<br />
                {order.shipping.line1}{order.shipping.line2 ? `, ${order.shipping.line2}` : ""}<br />
                {order.shipping.city}{order.shipping.state ? `, ${order.shipping.state}` : ""} {order.shipping.postal_code}<br />
                {order.shipping.country}
              </p>
            </div>

            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl mb-8">
              <Mail size={18} className="text-[#8b6f3d] mt-0.5" />
              <p className="text-sm text-zinc-700">
                {T.emailSent} <strong>{order.shipping.email}</strong>. {T.emailAlso}
              </p>
            </div>

            <div className="text-center">
              <Link to="/store"><Button className="bg-[#8b6f3d] hover:bg-[#a1854b] text-white rounded-full h-12 px-8">{T.continueShopping}</Button></Link>
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

const FR = {
  loading: "Chargement…",
  notFound: "Commande introuvable.",
  backToStore: "Retour à la boutique",
  tag: "Paiement confirmé",
  title: "Merci pour votre commande !",
  body: "Votre paiement a été traité avec succès. Nous préparons votre commande.",
  orderRef: "N° de commande :",
  itemsTitle: "Articles commandés",
  size: "Taille",
  subtotal: "Sous-total",
  shipping: "Livraison",
  shippingAddr: "Adresse de livraison",
  emailSent: "Un email de confirmation a été envoyé à",
  emailAlso: "Le propriétaire de la boutique a également été notifié.",
  continueShopping: "Continuer mes achats",
};
const EN = {
  loading: "Loading…",
  notFound: "Order not found.",
  backToStore: "Back to store",
  tag: "Payment confirmed",
  title: "Thank you for your order!",
  body: "Your payment has been processed. We are preparing your order.",
  orderRef: "Order #:",
  itemsTitle: "Order items",
  size: "Size",
  subtotal: "Subtotal",
  shipping: "Shipping",
  shippingAddr: "Shipping address",
  emailSent: "A confirmation email has been sent to",
  emailAlso: "The store owner has also been notified.",
  continueShopping: "Continue shopping",
};
